// Canonical mug-fill rating scale — the SINGLE source of truth for the
// brand-signature mug mechanic. These are the exact labels and colors users see
// when they RATE ("Rate a Visit" / MugRating and Quick Sip). Every screen that
// DISPLAYS a rating (feed, post detail, profile, shop pages, map, share card)
// must use these so the vocabulary and colors read identically everywhere and
// can never drift apart again.
//
// Bands (fill %): 0 slide · ≤20 Just a Sip · ≤40 Getting There · ≤60 Half Cup ·
// ≤80 Good Pour · ≤95 Almost Perfect · >95 Perfect Brew.

export function getFillLabel(fill: number): string {
  // 0 = no rating (vibe posts / empty avg) on viewing screens → blank. The
  // rating screens (MugRating/QuickSip) use getMugStyle, whose 0 = "Slide to rate".
  if (fill === 0) return ''
  if (fill <= 20) return 'Just a Sip'
  if (fill <= 40) return 'Getting There'
  if (fill <= 60) return 'Half Cup'
  if (fill <= 80) return 'Good Pour'
  if (fill <= 95) return 'Almost Perfect'
  return '✨ Perfect Brew'
}

// Sub-label shown beneath the main label on the rating screens.
export function getFillSubLabel(fill: number): string {
  if (fill === 0) return ''
  if (fill <= 20) return 'Not quite right'
  if (fill <= 40) return 'Room to improve'
  if (fill <= 60) return 'Decent visit'
  if (fill <= 80) return 'Really enjoyed it'
  if (fill <= 95) return 'Loved it'
  return 'Absolute favorite'
}

// Liquid fill color for the mug (single hex — used by the simpler mugs on
// viewing screens). Matches MugRating's `liquid` values exactly.
export function getMugColor(fill: number): string {
  if (fill === 0) return 'transparent'
  if (fill <= 20) return '#b0c4d4'
  if (fill <= 40) return '#c8924a'
  if (fill <= 60) return '#a06428'
  if (fill <= 80) return '#7a3e10'
  if (fill <= 95) return '#4e2008'
  return '#2e1004'
}

// Full style set (liquid + crema + glow + labels) for the animated mugs on the
// rating screens (MugRating / QuickSip), so those can share one definition too.
export function getMugStyle(fill: number): {
  liquid: string; crema: string; glow: string; label: string; sub: string
} {
  if (fill === 0) return { liquid: 'transparent', crema: 'transparent', glow: 'none', label: 'Slide to rate', sub: '' }
  if (fill <= 20) return { liquid: '#b0c4d4', crema: '#ccdde8', glow: 'rgba(176,196,212,0.25)', label: 'Just a Sip', sub: 'Not quite right' }
  if (fill <= 40) return { liquid: '#c8924a', crema: '#dba96a', glow: 'rgba(200,146,74,0.3)', label: 'Getting There', sub: 'Room to improve' }
  if (fill <= 60) return { liquid: '#a06428', crema: '#c07c38', glow: 'rgba(160,100,40,0.38)', label: 'Half Cup', sub: 'Decent visit' }
  if (fill <= 80) return { liquid: '#7a3e10', crema: '#9a5420', glow: 'rgba(122,62,16,0.45)', label: 'Good Pour', sub: 'Really enjoyed it' }
  if (fill <= 95) return { liquid: '#4e2008', crema: '#6e3410', glow: 'rgba(210,140,60,0.6)', label: 'Almost Perfect', sub: 'Loved it' }
  return { liquid: '#2e1004', crema: '#4e2008', glow: 'rgba(230,160,60,0.8)', label: '✨ Perfect Brew', sub: 'Absolute favorite' }
}
